import theme from 'ThemeProvider/theme';
import sx from 'utils/createSx';

export default sx({
  container: {
    minHeight: '100vh',
    backgroundColor: '#f9f9f9',
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
  },

  title: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.primary,
  },

  // Empty cart styles
  emptyContainer: {
    minHeight: '50vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
  },

  emptyMessage: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },

  shopButton: {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  // Items list styles
  itemsList: {
    padding: 0,
    '& .MuiListItem-root': {
      padding: 0,
      marginBottom: theme.spacing(2),
    },
  },

  itemCard: {
    borderRadius: theme.spacing(1),
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
    marginBottom: theme.spacing(1.5),
  },

  listItem: {
    padding: theme.spacing(2),
    alignItems: 'center',
    gap: theme.spacing(2),
  },

  beerImage: {
    width: 100,
    height: 100,
    borderRadius: theme.spacing(1),
    objectFit: 'contain',
    backgroundColor: theme.palette.grey[100],
    marginTop: theme.spacing(2),
  },

  beerName: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },

  originalPrice: {
    color: theme.palette.text.primary,
    fontWeight: 'normal',
  },

  premiumPrice: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },

  itemActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    minWidth: 120,
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },

  excludeButton: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    borderColor: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0.5, 1),
    '&:hover': {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
    },
  },

  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& button': {
      minWidth: 30,
      width: 30,
      height: 30,
      padding: 0,
    },
  },

  quantity: {
    minWidth: 30,
    textAlign: 'center',
    fontWeight: 'bold',
  },

  // Summary card styles
  summaryCard: {
    borderRadius: theme.spacing(1),
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
    position: 'sticky',
    top: theme.spacing(2),
  },

  // Coupon section
  couponSection: {
    marginBottom: theme.spacing(3),
  },

  couponTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },

  couponInputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },

  couponInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(0.5),
    },
  },

  couponButton: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },

  discountChip: {
    marginTop: theme.spacing(1),
  },

  couponAlert: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },

  couponSuccessContainer: {
    marginTop: theme.spacing(1),
  },

  // Price section
  priceSection: {
    marginBottom: theme.spacing(3),
  },

  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },

  discountText: {
    color: theme.palette.success.main,
  },

  discountAmount: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },

  subscriptionSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  subscriptionLink: {
    color: theme.palette.success.main,
    textDecoration: 'underline',
    fontSize: '1rem',
    fontWeight: 'bold',
    padding: 0,
    minWidth: 'auto',
    textAlign: 'left',
    justifyContent: 'flex-start',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'transparent',
    },
  },

  subscriptionPrice: {
    fontWeight: 'bold',
    color: theme.palette.success.main,
    fontSize: '1.25rem',
  },

  subscriberLabel: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    fontSize: '1rem',
  },

  finalPriceLabel: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },

  finalPrice: {
    fontWeight: 'bold',
    color: theme.palette.success.main,
  },

  divider: {
    margin: `${theme.spacing(2)} 0`,
  },

  // Action buttons
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  continueShoppingButton: {
    borderColor: theme.palette.text.secondary,
    color: theme.palette.text.secondary,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },

  proceedButton: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    padding: theme.spacing(1.5),
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },

  // Responsive design
  [theme.breakpoints.down('md')]: {
    container: {
      padding: theme.spacing(2),
    },

    title: {
      fontSize: '1.5rem',
      marginBottom: theme.spacing(3),
    },

    beerImage: {
      width: 60,
      height: 60,
    },

    itemActions: {
      alignItems: 'center',
    },

    couponInputContainer: {
      flexDirection: 'column',
    },

    summaryCard: {
      position: 'static',
      marginTop: theme.spacing(3),
    },
  },
});
